/**
 * Rebindable controls: what a pilot can change, and how it survives a reload.
 *
 * ## The shape of the problem
 *
 * There are two kinds of binding and they are not interchangeable:
 *
 *   - **Axes** — pitch, roll, yaw, throttle. These come from a keyboard *pair* (two keys that
 *     integrate an axis between them) or from a gamepad axis, and they carry a *shape*: a
 *     deadzone, an expo curve, and an inversion. The shape belongs to the binding rather than to
 *     the axis, because "pitch, inverted, with a lot of expo" is a property of this pilot's
 *     hardware and habits, not of the aeroplane.
 *   - **Actions** — flaps, gear, brakes, camera, pause. A key or a gamepad button, no shape.
 *
 * Modelling them separately is what keeps the rebinding UI honest: an axis row offers invert and
 * a curve, an action row does not, and neither can be bound to something it cannot read.
 *
 * ## Why bindings are data and not a `switch`
 *
 * `flightControls.ts` currently asks `keyboard.isDown('KeyW')` directly, which is the right
 * amount of machinery for a milestone with no settings screen and the wrong amount for one with
 * a rebinding UI. This is the table that replaces those literals: the defaults here *are* the
 * CLAUDE.md bindings, so the shipped controls are still one readable list, but every one of them
 * can now be replaced at runtime and written to storage.
 *
 * ## What "persist" has to mean
 *
 * A stored binding set is written by whatever build the pilot last ran. When a later build adds
 * an action, the stored set will not have it — so loading merges over the defaults rather than
 * replacing them, and an unknown action in storage is dropped rather than treated as an error.
 * The alternative is that adding one binding silently resets every other one, on exactly the
 * upgrade where a pilot would least expect it.
 *
 * `updateFixed` / `updateFrame`: neither. Read at start-up and when the rebinding UI commits.
 */

import {
  STORAGE_KEYS,
  isRecord,
  readJson,
  writeJson,
  type StorageService,
} from '../core/storage.js';
import type { AxisShapingOptions } from './axes.js';

/** The continuous controls a pilot can rebind. */
export const AXIS_IDS = ['pitch', 'roll', 'yaw', 'throttle'] as const;
export type AxisId = (typeof AXIS_IDS)[number];

/** The discrete controls a pilot can rebind. */
export const ACTION_IDS = [
  'flapsDown',
  'flapsUp',
  'gear',
  'brakes',
  'parkingBrake',
  'startEngine',
  'trimUp',
  'trimDown',
  'rudderTrimLeft',
  'rudderTrimRight',
  'aileronTrimLeft',
  'aileronTrimRight',
  'centreTrim',
  'cycleCamera',
  'cameraCockpit',
  'cameraChase',
  'cameraFlyby',
  'cameraOrbit',
  'cameraFree',
  'toggleHud',
  'toggleFighterHud',
  'toggleCoordination',
  'toggleMouseYoke',
  'recentreMouseYoke',
  'pause',
  'respawn',
  'timeOfDay',
  'airports',
  'mute',
  'perfOverlay',
  'help',
  'menu',
] as const;
export type ActionId = (typeof ACTION_IDS)[number];

/** How an axis is driven. */
export interface AxisBinding {
  /**
   * Keyboard pair: `negative` drives the axis toward −1, `positive` toward +1. `null` unbinds
   * the keyboard for this axis without unbinding the gamepad.
   */
  keys: { negative: string; positive: string } | null;
  /** Gamepad axis index, or `null` for none. */
  gamepadAxis: number | null;
  /** Invert the *device*, not the aircraft response. */
  invert: boolean;
  /** Expo, 0 = linear, 1 = fully cubic. See `axes.ts`. */
  expo: number;
  /** Deadzone as a fraction of travel, for the gamepad axis only. */
  deadzone: number;
}

/** How an action is triggered. */
export interface ActionBinding {
  key: string | null;
  gamepadButton: number | null;
}

export interface Bindings {
  axes: Record<AxisId, AxisBinding>;
  actions: Record<ActionId, ActionBinding>;
}

/**
 * The shipped bindings — the CLAUDE.md defaults, written once.
 *
 * WASD/arrows pitch + roll · Q/E rudder · −/= throttle · F flaps · G gear · B brakes ·
 * 1–5 cameras · C cycle view · P pause · T time of day · M mute · F3 perf overlay · Esc menu,
 * plus `,`/`.` elevator trim, `Z`/`X` rudder trim, `;`/`'` aileron trim, `J` auto-coordination
 * and `Y` the mouse yoke.
 *
 * The arrow keys are the *second* binding on pitch and roll rather than the first, and the input
 * layer accepts either — see `flightControls.ts`. Only one of them is shown in the rebinding UI,
 * because a UI that offered two rows per axis would be describing an implementation detail.
 */
export function defaultBindings(): Bindings {
  return {
    axes: {
      pitch: {
        keys: { negative: 'KeyS', positive: 'KeyW' },
        gamepadAxis: 1,
        // The stick's own convention: forward is negative, and forward is nose-down.
        invert: true,
        expo: 0.35,
        deadzone: 0.08,
      },
      roll: {
        keys: { negative: 'KeyA', positive: 'KeyD' },
        gamepadAxis: 0,
        invert: false,
        expo: 0.4,
        deadzone: 0.08,
      },
      yaw: {
        keys: { negative: 'KeyQ', positive: 'KeyE' },
        gamepadAxis: 2,
        invert: false,
        expo: 0.35,
        deadzone: 0.12,
      },
      throttle: {
        keys: { negative: 'Minus', positive: 'Equal' },
        gamepadAxis: 3,
        invert: true,
        expo: 0,
        deadzone: 0.15,
      },
    },
    actions: {
      flapsDown: { key: 'KeyF', gamepadButton: 2 },
      flapsUp: { key: 'KeyV', gamepadButton: 1 },
      gear: { key: 'KeyG', gamepadButton: null },
      brakes: { key: 'KeyB', gamepadButton: 0 },
      parkingBrake: { key: 'KeyK', gamepadButton: null },
      startEngine: { key: 'KeyI', gamepadButton: null },
      trimUp: { key: 'Period', gamepadButton: 12 },
      trimDown: { key: 'Comma', gamepadButton: 13 },
      rudderTrimLeft: { key: 'KeyZ', gamepadButton: null },
      rudderTrimRight: { key: 'KeyX', gamepadButton: null },
      aileronTrimLeft: { key: 'Semicolon', gamepadButton: null },
      aileronTrimRight: { key: 'Quote', gamepadButton: null },
      centreTrim: { key: 'Backslash', gamepadButton: null },
      cycleCamera: { key: 'KeyC', gamepadButton: 3 },
      cameraCockpit: { key: 'Digit1', gamepadButton: null },
      cameraChase: { key: 'Digit2', gamepadButton: null },
      cameraFlyby: { key: 'Digit3', gamepadButton: null },
      cameraOrbit: { key: 'Digit4', gamepadButton: null },
      cameraFree: { key: 'Digit5', gamepadButton: null },
      toggleHud: { key: 'KeyH', gamepadButton: null },
      toggleFighterHud: { key: 'KeyO', gamepadButton: null },
      toggleCoordination: { key: 'KeyJ', gamepadButton: null },
      toggleMouseYoke: { key: 'KeyY', gamepadButton: null },
      recentreMouseYoke: { key: 'KeyN', gamepadButton: null },
      pause: { key: 'KeyP', gamepadButton: 9 },
      respawn: { key: 'KeyR', gamepadButton: null },
      timeOfDay: { key: 'KeyT', gamepadButton: null },
      airports: { key: 'KeyL', gamepadButton: null },
      mute: { key: 'KeyM', gamepadButton: null },
      perfOverlay: { key: 'F3', gamepadButton: null },
      help: { key: 'Slash', gamepadButton: null },
      menu: { key: 'Escape', gamepadButton: 8 },
    },
  };
}

/** Human-readable labels for the rebinding UI, grouped the way a pilot thinks about them. */
export const AXIS_LABELS: Readonly<Record<AxisId, string>> = Object.freeze({
  pitch: 'Pitch (elevator)',
  roll: 'Roll (ailerons)',
  yaw: 'Yaw (rudder)',
  throttle: 'Throttle',
});

export const ACTION_GROUPS: readonly { title: string; actions: readonly ActionId[] }[] = [
  { title: 'Aircraft', actions: ['flapsDown', 'flapsUp', 'gear', 'brakes', 'parkingBrake', 'startEngine'] },
  {
    title: 'Trim',
    actions: [
      'trimUp',
      'trimDown',
      'rudderTrimLeft',
      'rudderTrimRight',
      'aileronTrimLeft',
      'aileronTrimRight',
      'centreTrim',
    ],
  },
  {
    title: 'Cameras',
    actions: ['cycleCamera', 'cameraCockpit', 'cameraChase', 'cameraFlyby', 'cameraOrbit', 'cameraFree'],
  },
  {
    title: 'Display',
    actions: ['toggleHud', 'toggleFighterHud', 'perfOverlay', 'help', 'timeOfDay'],
  },
  {
    title: 'Aids and session',
    actions: [
      'toggleCoordination',
      'toggleMouseYoke',
      'recentreMouseYoke',
      'airports',
      'respawn',
      'pause',
      'mute',
      'menu',
    ],
  },
];

export const ACTION_LABELS: Readonly<Record<ActionId, string>> = Object.freeze({
  flapsDown: 'Flaps down one detent',
  flapsUp: 'Flaps up one detent',
  gear: 'Landing gear',
  brakes: 'Wheel brakes (hold)',
  parkingBrake: 'Parking brake',
  startEngine: 'Starter / shut down',
  trimUp: 'Elevator trim nose up',
  trimDown: 'Elevator trim nose down',
  rudderTrimLeft: 'Rudder trim left',
  rudderTrimRight: 'Rudder trim right',
  aileronTrimLeft: 'Aileron trim left',
  aileronTrimRight: 'Aileron trim right',
  centreTrim: 'Centre rudder and aileron trim',
  cycleCamera: 'Cycle camera',
  cameraCockpit: 'Cockpit view',
  cameraChase: 'Chase view',
  cameraFlyby: 'Fly-by view',
  cameraOrbit: 'Orbit view',
  cameraFree: 'Free / drone view',
  toggleHud: 'Debug flight readout',
  toggleFighterHud: 'Fighter HUD overlay',
  toggleCoordination: 'Auto-coordination',
  toggleMouseYoke: 'Mouse yoke',
  recentreMouseYoke: 'Recentre mouse yoke',
  pause: 'Pause',
  respawn: 'Respawn at the last airport',
  timeOfDay: 'Time and weather panel',
  airports: 'Airport picker',
  mute: 'Mute',
  perfOverlay: 'Performance overlay',
  help: 'Control hints',
  menu: 'Menu / back',
});

/** The shaping a bound axis produces, for `shapeAxis`. */
export function shapingFor(binding: AxisBinding): AxisShapingOptions {
  return { deadzone: binding.deadzone, expo: binding.expo, invert: binding.invert };
}

/**
 * Every binding that collides with another, as a set of ids.
 *
 * Reported rather than prevented. A pilot who wants `B` on both brakes and something else has a
 * reason, and a rebinding UI that silently refuses is worse than one that shows the clash — but
 * a clash they cannot *see* is worst of all, which is what this is for.
 */
export function conflicts(bindings: Bindings): Set<ActionId> {
  const byKey = new Map<string, ActionId[]>();
  for (const id of ACTION_IDS) {
    const key = bindings.actions[id].key;
    if (key === null) continue;
    const list = byKey.get(key);
    if (list === undefined) byKey.set(key, [id]);
    else list.push(id);
  }
  const clashing = new Set<ActionId>();
  for (const list of byKey.values()) {
    if (list.length > 1) for (const id of list) clashing.add(id);
  }
  return clashing;
}

/**
 * A key code as a pilot would recognise it on their keyboard.
 *
 * `KeyboardEvent.code` is a *physical* position — `KeyQ` is wherever Q sits on a US layout — so
 * it is the right thing to bind against (a rebind stays in the same place on any layout) and the
 * wrong thing to show. This is the translation, and it stops at the point of diminishing returns:
 * the common families are handled by rule, and anything exotic falls through as its own code,
 * which is at least unambiguous.
 */
export function keyLabel(code: string | null): string {
  if (code === null) return '—';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  if (code.startsWith('Arrow')) return `${code.slice(5)} arrow`;
  const named: Record<string, string> = {
    Minus: '−',
    Equal: '=',
    Comma: ',',
    Period: '.',
    Slash: '/',
    Backslash: '\\',
    Semicolon: ';',
    Quote: "'",
    BracketLeft: '[',
    BracketRight: ']',
    Space: 'Space',
    Escape: 'Esc',
    Enter: 'Enter',
    Tab: 'Tab',
    Backquote: '`',
    ShiftLeft: 'L Shift',
    ShiftRight: 'R Shift',
    ControlLeft: 'L Ctrl',
    ControlRight: 'R Ctrl',
    AltLeft: 'L Alt',
    AltRight: 'R Alt',
  };
  return named[code] ?? code;
}

/** A gamepad button index as a pilot would recognise it. Standard layout. */
export function gamepadButtonLabel(index: number | null): string {
  if (index === null) return '—';
  const named: Record<number, string> = {
    0: 'A / ✕',
    1: 'B / ○',
    2: 'X / □',
    3: 'Y / △',
    4: 'LB',
    5: 'RB',
    6: 'LT',
    7: 'RT',
    8: 'Back',
    9: 'Start',
    12: 'D-pad up',
    13: 'D-pad down',
    14: 'D-pad left',
    15: 'D-pad right',
  };
  return named[index] ?? `Button ${index}`;
}

/** A gamepad axis index as a pilot would recognise it. */
export function gamepadAxisLabel(index: number | null): string {
  if (index === null) return '—';
  const named: Record<number, string> = {
    0: 'Left stick X',
    1: 'Left stick Y',
    2: 'Right stick X',
    3: 'Right stick Y',
  };
  return named[index] ?? `Axis ${index}`;
}

/**
 * Owns the bindings and persists them.
 *
 * Deliberately not an `Emitter`: a binding change is not something a subsystem reacts to
 * continuously, it is something the input layer re-reads. `FlightControls` holds a reference to
 * this and looks up whatever is current on each tick, so a rebind takes effect on the next frame
 * with no subscription anywhere.
 */
export class BindingStore {
  private readonly storage: StorageService;
  private current: Bindings;

  constructor(storage: StorageService) {
    this.storage = storage;
    this.current = defaultBindings();
  }

  get bindings(): Bindings {
    return this.current;
  }

  axis(id: AxisId): AxisBinding {
    return this.current.axes[id];
  }

  action(id: ActionId): ActionBinding {
    return this.current.actions[id];
  }

  /** The key bound to an action, or null. The hot path for the keyboard layer. */
  keyFor(id: ActionId): string | null {
    return this.current.actions[id].key;
  }

  setAxis(id: AxisId, changes: Partial<AxisBinding>): void {
    this.current.axes[id] = { ...this.current.axes[id], ...changes };
    this.save();
  }

  setAction(id: ActionId, changes: Partial<ActionBinding>): void {
    this.current.actions[id] = { ...this.current.actions[id], ...changes };
    this.save();
  }

  reset(): void {
    this.current = defaultBindings();
    this.save();
  }

  /** Merge whatever was stored over the defaults. Unknown ids are dropped. */
  load(): void {
    const stored = readJson(this.storage, STORAGE_KEYS.bindings, isRecord);
    if (stored === null) return;
    this.current = merge(stored, defaultBindings());
  }

  save(): boolean {
    return writeJson(this.storage, STORAGE_KEYS.bindings, this.current);
  }
}

function merge(stored: Record<string, unknown>, defaults: Bindings): Bindings {
  const axes = isRecord(stored['axes']) ? stored['axes'] : {};
  const actions = isRecord(stored['actions']) ? stored['actions'] : {};

  for (const id of AXIS_IDS) {
    const raw = axes[id];
    if (!isRecord(raw)) continue;
    const fallback = defaults.axes[id];
    defaults.axes[id] = {
      keys: readKeyPair(raw['keys'], fallback.keys),
      gamepadAxis: readIndex(raw['gamepadAxis'], fallback.gamepadAxis, 0, 15),
      invert: typeof raw['invert'] === 'boolean' ? raw['invert'] : fallback.invert,
      expo: readUnit(raw['expo'], fallback.expo),
      deadzone: readUnit(raw['deadzone'], fallback.deadzone),
    };
  }

  for (const id of ACTION_IDS) {
    const raw = actions[id];
    if (!isRecord(raw)) continue;
    const fallback = defaults.actions[id];
    defaults.actions[id] = {
      key: raw['key'] === null ? null : typeof raw['key'] === 'string' ? raw['key'] : fallback.key,
      gamepadButton: readIndex(raw['gamepadButton'], fallback.gamepadButton, 0, 19),
    };
  }

  return defaults;
}

function readKeyPair(
  value: unknown,
  fallback: AxisBinding['keys'],
): AxisBinding['keys'] {
  if (value === null) return null;
  if (!isRecord(value)) return fallback;
  const negative = value['negative'];
  const positive = value['positive'];
  if (typeof negative !== 'string' || typeof positive !== 'string') return fallback;
  return { negative, positive };
}

function readIndex(value: unknown, fallback: number | null, min: number, max: number): number | null {
  if (value === null) return null;
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) return fallback;
  return value;
}

function readUnit(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}
